import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  UsersIcon,
  ShieldExclamationIcon,
  LightBulbIcon,
  FlagIcon,
  ChatBubbleLeftEllipsisIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts'
import api from '../../services/api'

interface Stats {
  active_clients: number
  total_users: number
  new_this_week: number
  pending_kyc: number
  unsigned_agreements: number
  pending_risk_profiles: number
  pending_onboarding: number
  open_recommendations: number
  open_complaints: number
  monthly_revenue: number
  total_revenue: number
  active_subscriptions: number
  plan_breakdown: Record<string, number>
}

type RevenuePeriod = '1w' | '1m' | '6m' | '1y' | 'custom'

interface Trends {
  revenue_trend: { month: string; revenue: number }[]
  kyc_breakdown: { approved: number; under_review: number; rejected: number; not_started: number }
  subscription_breakdown: { active: number; expired: number; cancelled: number; suspended: number }
}

function CompactStat({
  label, value, meta, metaColor = 'text-gray-400', icon: Icon, iconBg, href,
}: {
  label: string; value: number | string; meta?: string; metaColor?: string;
  icon: React.ComponentType<any>; iconBg: string; href?: string;
}) {
  const inner = (
    <div className="flex items-center gap-3 bg-dark-800 border border-dark-700 rounded-xl px-4 py-3 hover:border-dark-600 hover:bg-dark-700/50 transition-colors h-full">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon style={{ width: 18, height: 18 }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="text-lg font-bold text-white leading-none">{value}</p>
          {meta && <span className={`text-[11px] font-medium ${metaColor} whitespace-nowrap`}>{meta}</span>}
        </div>
        <p className="text-xs text-gray-400 mt-1 truncate">{label}</p>
      </div>
    </div>
  )
  return (
    <div className="flex-1 min-w-[190px]">
      {href ? <Link to={href}>{inner}</Link> : inner}
    </div>
  )
}

const ALLOWED = [
  'Research rationale, strategy logic, signals, entry/exit, stop-loss, time horizon.',
]
const RESTRICTED = [
  'Trade execution, account access, credentials, algo operation on client behalf.',
]

const KYC_COLORS: Record<string, string> = {
  approved: '#22c55e',
  under_review: '#f59e0b',
  rejected: '#ef4444',
  not_started: '#475569',
}
const KYC_LABELS: Record<string, string> = {
  approved: 'Approved',
  under_review: 'Under Review',
  rejected: 'Rejected',
  not_started: 'Not Started',
}

const SUB_COLORS: Record<string, string> = {
  active: '#22c55e',
  expired: '#475569',
  cancelled: '#ef4444',
  suspended: '#f59e0b',
}
const SUB_LABELS: Record<string, string> = {
  active: 'Active',
  expired: 'Expired',
  cancelled: 'Cancelled',
  suspended: 'Suspended',
}

function DonutCard({
  title, data, colors, labels, centerLabel,
}: {
  title: string
  data: Record<string, number>
  colors: Record<string, string>
  labels: Record<string, string>
  centerLabel: string
}) {
  const entries = Object.entries(data).filter(([, v]) => v > 0)
  const total = entries.reduce((sum, [, v]) => sum + v, 0)

  return (
    <div className="bg-dark-800 rounded-2xl p-6 shadow-sm border border-dark-700 h-full flex flex-col">
      <h3 className="text-sm font-semibold text-gray-300 mb-3">{title}</h3>
      {total === 0 ? (
        <div className="flex-1 flex items-center justify-center text-xs text-gray-500 py-8">
          No data yet
        </div>
      ) : (
        <>
          <div className="relative h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={entries.map(([key, value]) => ({ name: labels[key] || key, value, key }))}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={68}
                  paddingAngle={2}
                  stroke="none"
                >
                  {entries.map(([key]) => (
                    <Cell key={key} fill={colors[key] || '#64748b'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#e2e8f0' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-white">{total}</span>
              <span className="text-[10px] text-gray-400">{centerLabel}</span>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {entries.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: colors[key] || '#64748b' }} />
                  <span className="text-gray-400">{labels[key] || key}</span>
                </div>
                <span className="font-semibold text-gray-100">{value}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [trends, setTrends] = useState<Trends | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [revenuePeriod, setRevenuePeriod] = useState<RevenuePeriod>('6m')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [trendsLoading, setTrendsLoading] = useState(false)

  const fetchTrends = async (period: RevenuePeriod, from?: string, to?: string) => {
    setTrendsLoading(true)
    try {
      const params: Record<string, string> = { period }
      if (period === 'custom' && from && to) {
        params.date_from = from
        params.date_to = to
      }
      const res = await api.get('/admin/stats/trends', { params })
      setTrends(res.data)
    } catch {}
    finally { setTrendsLoading(false) }
  }

  const fetchStats = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get('/admin/stats')
      setStats(res.data)
    } catch {
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const refreshAll = () => {
    fetchStats()
    fetchTrends(revenuePeriod, customFrom, customTo)
  }

  useEffect(() => { fetchStats() }, [])
  useEffect(() => {
    if (revenuePeriod !== 'custom') fetchTrends(revenuePeriod)
    else if (customFrom && customTo) fetchTrends('custom', customFrom, customTo)
  }, [revenuePeriod])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-2"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; Overview</nav>
          <h1 className="text-3xl font-bold text-white mb-1">Client Advisory Dashboard</h1>
          <p className="text-sm text-gray-400 max-w-xl">
            Recordkeeping, suitability, recommendation logs, and grievance monitoring — SEBI-compliant.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {['Advisory Scope Only', 'Fixed Fee Model', 'Audit Ready'].map(tag => (
              <span key={tag} className="text-xs px-3 py-1 rounded-full border border-teal-500/20 text-teal-400 bg-teal-500/10 font-medium">
                {tag}
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Link
            to="/admin/content"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            <PlusIcon style={{ width: 16, height: 16 }} />
            Add Recommendation
          </Link>
          <button
            onClick={refreshAll}
            className="px-3 py-2.5 rounded-xl border border-dark-600 bg-dark-800 text-gray-300 hover:bg-dark-700 transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon style={{ width: 16, height: 16 }} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}

      {/* Compact KPI strip */}
      <div className="flex flex-wrap gap-3">
        <CompactStat
          label="Active Clients"
          value={loading ? '—' : stats?.active_clients ?? 0}
          meta={stats ? `+${stats.new_this_week} this wk` : undefined}
          metaColor="text-emerald-400"
          icon={UsersIcon}
          iconBg="bg-blue-500/10 text-blue-400"
          href="/admin/clients"
        />
        <CompactStat
          label="Pending KYC"
          value={loading ? '—' : stats?.pending_onboarding ?? 0}
          meta={stats && stats.pending_onboarding > 0 ? 'Needs action' : undefined}
          metaColor="text-orange-400"
          icon={ShieldExclamationIcon}
          iconBg="bg-orange-500/10 text-orange-400"
          href="/admin/kyc"
        />
        <CompactStat
          label="Open Recommendations"
          value={loading ? '—' : stats?.open_recommendations ?? 0}
          icon={LightBulbIcon}
          iconBg="bg-violet-500/10 text-violet-400"
          href="/admin/content"
        />
        <CompactStat
          label="Audit Flags"
          value={loading ? '—' : stats?.pending_kyc ?? 0}
          icon={FlagIcon}
          iconBg="bg-yellow-500/10 text-yellow-400"
          href="/admin/audit-trail"
        />
        <CompactStat
          label="SCORES Complaints"
          value={loading ? '—' : stats?.open_complaints ?? 0}
          icon={ChatBubbleLeftEllipsisIcon}
          iconBg="bg-green-500/10 text-green-400"
          href="/admin/complaints"
        />
      </div>

      {/* Revenue trend + KYC donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-dark-800 rounded-2xl p-6 shadow-sm border border-dark-700">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <ArrowTrendingUpIcon className="text-gray-400" style={{ width: 16, height: 16 }} />
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Revenue Trend</span>
            </div>
            <div className="flex items-center gap-1 bg-dark-900 border border-dark-600 rounded-lg p-0.5">
              {(['1w', '1m', '6m', '1y', 'custom'] as RevenuePeriod[]).map(p => (
                <button
                  key={p}
                  onClick={() => setRevenuePeriod(p)}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-md transition-colors ${revenuePeriod === p ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  {p === 'custom' ? 'Custom' : p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {revenuePeriod === 'custom' && (
            <div className="flex items-center gap-2 mb-3">
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="text-xs bg-dark-900 border border-dark-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <span className="text-xs text-gray-500">to</span>
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className="text-xs bg-dark-900 border border-dark-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button
                onClick={() => customFrom && customTo && fetchTrends('custom', customFrom, customTo)}
                disabled={!customFrom || !customTo}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-40 transition-colors"
              >
                Apply
              </button>
            </div>
          )}

          <div className="flex items-baseline gap-3 mb-4">
            <p className="text-2xl font-bold text-white">
              ₹{loading ? '—' : (stats?.monthly_revenue || 0).toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-gray-400">this month · Total ₹{loading ? '—' : (stats?.total_revenue || 0).toLocaleString('en-IN')}</p>
          </div>
          <div className="h-56">
            {trendsLoading ? (
              <div className="h-full flex items-center justify-center text-xs text-gray-500">Loading…</div>
            ) : trends && trends.revenue_trend.some(t => t.revenue > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends.revenue_trend} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#334155' }} tickLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={50}
                    tickFormatter={(v) => v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#e2e8f0' }}
                    formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} fill="url(#revenueFill)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-gray-500">
                No revenue recorded in this period
              </div>
            )}
          </div>
        </div>

        <DonutCard
          title="KYC Status"
          data={trends?.kyc_breakdown || { approved: 0, under_review: 0, rejected: 0, not_started: 0 }}
          colors={KYC_COLORS}
          labels={KYC_LABELS}
          centerLabel="Clients"
        />
      </div>

      {/* Compliance Snapshot + Subscription donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Compliance Snapshot */}
        <div className="lg:col-span-2 bg-dark-800 rounded-2xl p-6 shadow-sm border border-dark-700">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-base font-bold text-white">Compliance Snapshot</h2>
              <p className="text-xs text-gray-400 mt-0.5">Core rules enforced in day-to-day advisory workflow.</p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-400 bg-green-500/10 px-2.5 py-1 rounded-full border border-green-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
              Live
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-green-500/10 border border-green-500/20 p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircleIcon className="text-green-400 flex-shrink-0" style={{ width: 18, height: 18 }} />
                <span className="text-sm font-semibold text-gray-100">Allowed</span>
              </div>
              {ALLOWED.map(t => (
                <p key={t} className="text-xs text-gray-400 leading-relaxed">{t}</p>
              ))}
            </div>
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4">
              <div className="flex items-center gap-2 mb-2">
                <XCircleIcon className="text-red-400 flex-shrink-0" style={{ width: 18, height: 18 }} />
                <span className="text-sm font-semibold text-gray-100">Restricted</span>
              </div>
              {RESTRICTED.map(t => (
                <p key={t} className="text-xs text-gray-400 leading-relaxed">{t}</p>
              ))}
            </div>
          </div>

          {/* SEBI compliance checklist */}
          <div className="mt-4 pt-4 border-t border-dark-700 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Risk Profile on file', ok: stats ? stats.pending_risk_profiles === 0 : false },
              { label: 'Agreement eSigned', ok: stats ? stats.unsigned_agreements === 0 : false },
              { label: 'KYC Verified (CAMS)', ok: trends ? trends.kyc_breakdown.approved > 0 && trends.kyc_breakdown.under_review === 0 && trends.kyc_breakdown.not_started === 0 : false },
              { label: 'Fee Disclosure', ok: true },
              { label: 'No Execution Access', ok: true },
              { label: 'SCORES Compliant', ok: stats ? stats.open_complaints === 0 : false },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${item.ok ? 'bg-green-500/20' : 'bg-orange-500/20'}`}>
                  <div className={`w-2 h-2 rounded-full ${item.ok ? 'bg-green-500' : 'bg-orange-400'}`} />
                </div>
                <span className="text-xs text-gray-400">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <DonutCard
          title="Subscriptions"
          data={trends?.subscription_breakdown || { active: 0, expired: 0, cancelled: 0, suspended: 0 }}
          colors={SUB_COLORS}
          labels={SUB_LABELS}
          centerLabel="Total"
        />
      </div>

      {/* Plan breakdown */}
      <div className="bg-dark-800 rounded-2xl p-6 shadow-sm border border-dark-700">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">Active Subscriptions by Plan</h3>
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : stats?.plan_breakdown && Object.entries(stats.plan_breakdown).length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Object.entries(stats.plan_breakdown).map(([name, count]) => (
              <div key={name} className="flex flex-col">
                <span className="text-xs text-gray-400">{name}</span>
                <span className="text-xl font-bold text-gray-100">{count}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">No active subscriptions</p>
        )}
        {stats && (
          <div className="border-t border-dark-700 mt-4 pt-3 flex justify-between">
            <span className="text-xs font-medium text-gray-400">Total Active</span>
            <span className="text-sm font-bold text-white">{stats.active_subscriptions}</span>
          </div>
        )}
      </div>
    </div>
  )
}
